import React, { useEffect } from 'react';
import {
	createBrowserRouter,
	RouterProvider,
	Outlet,
	useLocation,
	Navigate,
} from 'react-router-dom';
import Header from './components/Header';
import AdminSubnav from './components/AdminSubnav';
import PrivateRoute from './components/privateRoute';
import SalesForm from './sections/Form';
import AdminLayout from './sections/pageSections/Admin/AdminLayout';
import Users from './sections/pageSections/Admin/Users';
import Errors from './sections/pageSections/Admin/Errors';
import Dashboard from './sections/pageSections/Admin/Dashboard';
import Orders from './sections/pageSections/Orders';
import Profile from './sections/pageSections/Profile';
import Review from './sections/pageSections/Review';
import RegisterForm from './sections/subsections/RegisterForm';
import SetPasswordForm from './sections/subsections/SetPasswordForm';
import LoginForm from './sections/subsections/loginForm';
import NotFound from './sections/pageSections/NotFound';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setupAxiosInterceptors } from './utils/axiosConfig';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { useSessionWarning } from './hooks/useSessionWarning';
import MailServerWarning from './components/MailServerWarning';
import ClientVersionNotice from './components/ClientVersionNotice';

import 'sweetalert2/dist/sweetalert2.min.css';
import './assets/scss/main.scss';

const RootLayout = () => {
	const location = useLocation();
	const { logout, refreshToken, devMailMode, userRole } = useAuth();

	const hideHeader =
		location.pathname === '/login' ||
		location.pathname === '/set-password';
	const showHeader = !hideHeader;
	const showAdminSubnav =
		showHeader && ['admin', 'superAdmin'].includes(userRole);
	const mainContentClassName = `mainContent${
		showAdminSubnav ? ' mainContent--admin' : ''
	}`;

	useEffect(() => {
		setupAxiosInterceptors(logout, refreshToken);
	}, [logout, refreshToken]);

	useTokenRefresh();
	useSessionWarning();

	return (
		<div className={`App ${devMailMode ? 'dev-mail-mode' : ''}`}>
			<ClientVersionNotice />
			<MailServerWarning />
			{showHeader && (
				<header className='App-header'>
					<Header />
				</header>
			)}
			{showAdminSubnav ? <AdminSubnav /> : null}
			<div className={showHeader ? mainContentClassName : ''}>
				<Outlet />
			</div>
		</div>
	);
};

const router = createBrowserRouter([
	{
		path: '/',
		element: <RootLayout />,
		children: [
			{
				index: true,
				element: (
					<PrivateRoute>
						<SalesForm />
					</PrivateRoute>
				),
			},
			{ path: 'login', element: <LoginForm /> },
			{
				path: 'reguser',
				element: <Navigate to='/admin/reguser' replace />,
			},
			{ path: 'set-password', element: <SetPasswordForm /> },
			{
				path: 'admin',
				element: (
					<PrivateRoute>
						<AdminLayout />
					</PrivateRoute>
				),
				children: [
					{
						index: true,
						element: <Navigate to='/admin/users' replace />,
					},
					{ path: 'users', element: <Users /> },
					{ path: 'reguser', element: <RegisterForm /> },
					{ path: 'errors', element: <Errors /> },
				],
			},
			{
				path: 'review',
				element: (
					<PrivateRoute>
						<Review />
					</PrivateRoute>
				),
			},
			{
				path: 'statistikk',
				element: (
					<PrivateRoute>
						<Dashboard />
					</PrivateRoute>
				),
			},
			{
				path: 'orders',
				element: (
					<PrivateRoute>
						<Orders />
					</PrivateRoute>
				),
			},
			{
				path: 'profile',
				element: (
					<PrivateRoute>
						<Profile />
					</PrivateRoute>
				),
			},
			{ path: '404', element: <NotFound /> },
			{ path: '*', element: <NotFound /> },
		],
	},
]);

function App() {
	return (
		<AuthProvider>
			<RouterProvider router={router} />
		</AuthProvider>
	);
}

export default App;
